import React, { useState } from 'react';
import { DataBody } from '../../types';
import * as api from '../../services/api';
import FieldTooltip from '../shared/FieldTooltip';

/**
 * Settings for one data body: retention, rate limit, concurrency.
 *
 * Why this is a modal and not three columns on the dashboard
 * ---------------------------------------------------------
 * These three are static config. They are set once at build time and then
 * looked at roughly never, yet they were occupying three of the eight
 * dashboard columns — pushing the numbers Scott actually watches (today's
 * volume, delivery rate) into the narrow columns on the right. They stay
 * fully editable; they just stop taking daily airtime.
 *
 * Guardrails
 * ----------
 * Retention is a dropdown, not a free number field: arbitrary values have no
 * meaning downstream (the 90-day cold-storage archive boundary is real) and a
 * typo'd "3" instead of "30" quietly destroys 27 days of leads. Rate and
 * concurrency are bounded steppers with a plain-language translation
 * underneath, because "60" means nothing on its own and "≈ 5.2M records/day"
 * is the number a person can actually sanity-check. The same bounds are
 * enforced server-side in routes/dataBodies.ts — keep the two in step.
 */

// Keep in step with RETENTION_PERIODS / *_MIN / *_MAX in
// backend/src/api/routes/dataBodies.ts.
const RETENTION_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
  { value: 180, label: '180 days' },
  { value: 365, label: '365 days' },
  { value: 0, label: 'Forever' },
];

const RATE_MIN = 1;
const RATE_MAX = 1000;
const CONCURRENCY_MIN = 1;
const CONCURRENCY_MAX = 100;

/** Steppers move in useful increments, not by 1 all the way to 1000. */
function rateStep(current: number, dir: 1 | -1): number {
  const step = current >= 100 ? 10 : current >= 20 ? 5 : 1;
  return clamp(current + step * dir, RATE_MIN, RATE_MAX);
}
function concurrencyStep(current: number, dir: 1 | -1): number {
  const step = current >= 20 ? 5 : 1;
  return clamp(current + step * dir, CONCURRENCY_MIN, CONCURRENCY_MAX);
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

const COMPACT = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });

/**
 * "60/sec" is an abstraction; "≈ 5.2M records/day" is a number someone can
 * hold against their actual traffic and notice is wrong.
 */
function rateTranslation(perSecond: number): string {
  const perDay = perSecond * 60 * 60 * 24;
  return `≈ ${COMPACT.format(perDay)} records/day at a sustained rate`;
}

function concurrencyTranslation(n: number): string {
  return `Up to ${n} lead${n === 1 ? '' : 's'} processed at the same moment`;
}

function retentionTranslation(days: number): string {
  if (days === 0) {return 'Leads are kept indefinitely. Cold-storage access fees may apply.';}
  if (days > 90) {
    return `Leads are removed after ${days} days. Per-lead processing detail is archived to cold storage at the 90-day mark.`;
  }
  return `Leads are removed after ${days} days.`;
}

interface StepperProps {
  label: string;
  tooltip: string;
  value: number;
  min: number;
  max: number;
  onStep: (dir: 1 | -1) => void;
  onType: (raw: string) => void;
  translation: string;
  disabled?: boolean;
}

function Stepper({ label, tooltip, value, min, max, onStep, onType, translation, disabled }: StepperProps) {
  return (
    <div className="form-group settings-field">
      <label className="form-label">
        {label}
        <FieldTooltip text={tooltip} />
      </label>
      <div className="stepper">
        <button
          type="button"
          className="stepper-btn"
          onClick={() => onStep(-1)}
          disabled={disabled || value <= min}
          aria-label={`Decrease ${label}`}
        >
          −
        </button>
        <input
          className="stepper-input"
          type="number"
          inputMode="numeric"
          value={value}
          min={min}
          max={max}
          disabled={disabled}
          onChange={e => onType(e.target.value)}
          aria-label={label}
        />
        <button
          type="button"
          className="stepper-btn"
          onClick={() => onStep(1)}
          disabled={disabled || value >= max}
          aria-label={`Increase ${label}`}
        >
          +
        </button>
        <span className="stepper-bounds">{min}–{max}</span>
      </div>
      <div className="settings-translation">{translation}</div>
    </div>
  );
}

interface Props {
  body: DataBody;
  onClose: () => void;
  /** Called with the server's updated body so the dashboard can merge it in. */
  onSaved: (updated: DataBody) => void;
}

export default function DataBodySettingsModal({ body, onClose, onSaved }: Props) {
  const [retention, setRetention] = useState<number>(body.dataRetentionDays);
  const [rate, setRate] = useState<number>(body.rateLimitPerSecond);
  const [concurrency, setConcurrency] = useState<number>(body.concurrencyLimit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isLive = body.status === 'active';
  const dirty =
    retention !== body.dataRetentionDays ||
    rate !== body.rateLimitPerSecond ||
    concurrency !== body.concurrencyLimit;

  // Shrinking retention is the one change here that destroys data, and it
  // does so on the next retention sweep rather than on save — so it needs
  // calling out explicitly, not just the generic live-body banner.
  const shrinkingRetention =
    body.dataRetentionDays === 0
      ? retention !== 0
      : retention !== 0 && retention < body.dataRetentionDays;

  // Typed input is clamped on commit, not on keystroke — clamping as you
  // type makes it impossible to clear the field and retype a number.
  function commitTyped(raw: string, min: number, max: number, set: (n: number) => void) {
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) {return;}
    set(clamp(n, min, max));
  }

  async function handleSave() {
    if (!dirty) { onClose(); return; }
    setSaving(true);
    setError('');
    try {
      const updated = await api.updateDataBody(body.id, {
        dataRetentionDays: retention,
        rateLimitPerSecond: rate,
        concurrencyLimit: concurrency,
      });
      onSaved(updated);
      onClose();
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.detail ||
        err.message ||
        'Could not save settings'
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={() => !saving && onClose()}>
      <div className="modal modal-settings" onClick={e => e.stopPropagation()}>
        <h2 className="modal-title">Data Body Settings</h2>

        {isLive && (
          <div className="modal-warn-banner" role="alert">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <div>
              <strong>This data body is active and processing live data.</strong>
              <div style={{ fontSize: 13, marginTop: 4, color: 'var(--text-dark-secondary)' }}>
                Changes apply immediately.
              </div>
            </div>
          </div>
        )}

        {/* Identity rows are read-only here. Name is renamed inline from the
            dashboard; source is reassigned from the Sources directory. Showing
            them anyway is what makes this modal legible when you've opened
            three of them in a row. */}
        <div className="settings-identity">
          <div className="settings-identity-row">
            <span className="settings-identity-label">Name</span>
            <span className="settings-identity-value">{body.name}</span>
          </div>
          <div className="settings-identity-row">
            <span className="settings-identity-label">Source</span>
            <span className="settings-identity-value">
              {body.sourceName || <span className="settings-identity-unset">Unassigned</span>}
            </span>
          </div>
        </div>

        <div className="form-group settings-field">
          <label className="form-label">
            Retention
            <FieldTooltip text="How long leads stay accessible in this data body. After this period they are removed. Beyond 90 days, per-lead processing detail moves to cold storage at the 90-day mark." />
          </label>
          <select
            className="form-select"
            value={retention}
            disabled={saving}
            onChange={e => setRetention(parseInt(e.target.value, 10))}
          >
            {RETENTION_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <div className="settings-translation">{retentionTranslation(retention)}</div>
          {shrinkingRetention && (
            <div className="settings-danger" role="alert">
              Shortening retention deletes leads already past the new window on the
              next sweep. This cannot be undone.
            </div>
          )}
        </div>

        <Stepper
          label="Rate"
          tooltip="Maximum leads this data body accepts per second. Sources sending faster get a 429 telling them to slow down."
          value={rate}
          min={RATE_MIN}
          max={RATE_MAX}
          onStep={dir => setRate(c => rateStep(c, dir))}
          onType={raw => commitTyped(raw, RATE_MIN, RATE_MAX, setRate)}
          translation={rateTranslation(rate)}
          disabled={saving}
        />

        <Stepper
          label="Concurrency"
          tooltip="Maximum leads being processed simultaneously. Catches sources that open many parallel connections to get around the rate limit."
          value={concurrency}
          min={CONCURRENCY_MIN}
          max={CONCURRENCY_MAX}
          onStep={dir => setConcurrency(c => concurrencyStep(c, dir))}
          onType={raw => commitTyped(raw, CONCURRENCY_MIN, CONCURRENCY_MAX, setConcurrency)}
          translation={concurrencyTranslation(concurrency)}
          disabled={saving}
        />

        {error && (
          <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#dc2626', fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !dirty}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
